import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Editor } from '@tinymce/tinymce-react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineMenuUnfold } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteJournalTemplate,
  getJournalTemplates,
  saveJournalTemplate,
} from '../../stores/slices/journalTemplates/journalTemplatesActions';
import LoadingOverlay from '../LoadingOverlay';
import JournalList from './JournalList';
import * as yup from 'yup';
import DeleteAlert from '../Alerts/DeleteAlert';
import { setActiveJournal } from '../../stores/slices/journalTemplates/journalTemplatesSlice';
import API_URL from '../../utils/getAPIUrl';

const TINYMCE_API = process.env.REACT_APP_TINYMCE_KEY;

const JournalItem = () => {
  const { getButtonProps, getDisclosureProps, isOpen } = useDisclosure({
    defaultIsOpen: true,
  });

  const [isCreating, setIsCreating] = useState(false);
  const { activeJournal, journalLoading } = useSelector(
    state => state.journalTemplates
  );

  const { userToken } = useSelector(state => state.user);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editorValue, setEditorValue] = useState('');
  const editorRef = useRef(null);
  const cancelRef = useRef();
  const toast = useToast();
  const dispatch = useDispatch();
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlert,
    onClose: onCloseAlert,
  } = useDisclosure();

  const schema = yup
    .object({
      templateName: yup.string().required('Template name is required'),
    })
    .required();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const submitJournal = async data => {
    if (editorRef.current) {
      const res = await dispatch(
        saveJournalTemplate({
          entry: editorRef.current.getContent(),
          isCreate: isCreating,
          name: data.templateName,
          id: activeJournal ? activeJournal.id : null,
        })
      );

      if (res.type === 'journalTemplate/saveJournalTemplate/rejected') {
        toast({
          title: 'Something went wrong',
          description: 'Please try again later.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        if (isCreating) {
          reset();
          setIsCreating(false);
        }
        toast({
          description: isCreating
            ? 'Journal template has been created'
            : 'Journal template has been updated',
          colorScheme: 'primary',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
          variant: 'primary',
        });
      }
    }
  };

  const onDeleteItem = async () => {
    setIsDeleting(true);
    const res = await dispatch(
      deleteJournalTemplate({ id: activeJournal?.id })
    );

    onCloseAlert();
    setIsDeleting(false);
    if (res.type === 'journalTemplate/deleteJournalTemplate/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      dispatch(getJournalTemplates());
      dispatch(setActiveJournal(null));
      toast({
        description: 'Template has been deleted',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };
  useEffect(() => {
    dispatch(setActiveJournal(null));
  }, []);

  useEffect(() => {
    reset({
      templateName: activeJournal?.name,
    });
  }, [activeJournal]);

  const imageUploadHandler = (blobInfo, progress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;
      xhr.open('POST', `${API_URL}/journal_images`);
      xhr.setRequestHeader('Authorization', userToken);

      xhr.upload.onprogress = e => {
        progress((e.loaded / e.total) * 100);
      };

      xhr.onload = () => {
        if (xhr.status === 403) {
          reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          reject('HTTP Error: ' + xhr.status);
          return;
        }

        const json = JSON.parse(xhr.responseText);

        if (!json || typeof json.location != 'string') {
          reject('Invalid JSON: ' + xhr.responseText);
          return;
        }

        resolve(json.location);
      };

      xhr.onerror = () => {
        reject(
          'Image upload failed due to a XHR Transport error. Code: ' +
            xhr.status
        );
      };

      const formData = new FormData();
      formData.append(
        'journal_image[image_data]',
        blobInfo.blob(),
        blobInfo.filename()
      );

      xhr.send(formData);
    });
  return (
    <React.Fragment>
      <Box minW={'300px'} overflow="hidden" {...getDisclosureProps()}>
        <JournalList h={'full'} />
      </Box>
      <Box w={'full'} overflowX={'hidden'} overflowY={'auto'}>
        <VStack spacing={'4'} h={'full'}>
          <Stack
            justifyContent={'space-between'}
            alignContent={'center'}
            direction={{ base: 'column', sm: 'row' }}
            w={'full'}
            spacing={4}
            pr={'2'}
            pb={'2'}
            zIndex={'10'}
            sx={{
              position: '-webkit-sticky',
              /* Safari */
              position: 'sticky',
              top: '0',
            }}
            bg={'white'}
            borderBottom={'1px'}
            borderColor={'gray.500'}
          >
            <HStack>
              <IconButton
                aria-label="Menu"
                display={'inline-flex'}
                icon={<AiOutlineMenuUnfold />}
                variant={'outline'}
                size="xs"
                {...getButtonProps()}
              />
            </HStack>
            <HStack>
              <Button
                size={'sm'}
                disabled={
                  journalLoading ||
                  isSubmitting ||
                  (!isCreating && !activeJournal)
                }
                isLoading={isSubmitting}
                onClick={handleSubmit(submitJournal)}
              >
                Save
              </Button>
              {isCreating && (
                <Button
                  size={'sm'}
                  bg={'red.400'}
                  _hover={{
                    color: 'red.400',
                    bg: 'transparent',
                  }}
                  disabled={journalLoading}
                  onClick={() => {
                    setIsCreating(false);
                    dispatch(setActiveJournal(null));
                    reset();
                  }}
                >
                  Cancel
                </Button>
              )}
              {!isCreating && (
                <Button
                  size={'sm'}
                  disabled={journalLoading}
                  onClick={() => {
                    dispatch(setActiveJournal(null));
                    setIsCreating(true);
                    reset({
                      templateName: '',
                    });
                  }}
                >
                  Create
                </Button>
              )}
              <Button
                size={'sm'}
                disabled={!activeJournal}
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={onOpenAlert}
              >
                Delete
              </Button>
            </HStack>
          </Stack>
          <Box
            overflowX={'auto'}
            w={'full'}
            flex={'1'}
            flexGrow={'1'}
            h={'full'}
            position={'relative'}
            border={activeJournal || isCreating ? '1px solid' : '0'}
            borderColor={'lightGray.500'}
            borderRadius={'6'}
          >
            {/* loading && <LoadingOverlay /> */}
            {!activeJournal && !isCreating ? (
              <Text fontWeight={'bold'} textAlign={'center'}>
                Please select a template or{' '}
                <Button
                  variant="link"
                  color={'primary'}
                  onClick={() => setIsCreating(true)}
                >
                  create
                </Button>{' '}
                a new one.
              </Text>
            ) : (
              <form style={{ height: '100%' }}>
                <Box p={'4'}>
                  <FormControl
                    id="templateName"
                    isInvalid={errors.templateName}
                  >
                    <FormLabel>Template name</FormLabel>
                    <Input id="templateName" {...register('templateName')} />
                    <FormErrorMessage>
                      {errors.templateName && errors.templateName.message}
                    </FormErrorMessage>
                  </FormControl>
                </Box>
                <Divider />
                <Box w={'full'} h={'80%'}>
                  <Editor
                    apiKey={TINYMCE_API}
                    onInit={(evt, editor) => {
                      editorRef.current = editor;
                      editorRef.current.setContent(
                        activeJournal ? activeJournal.entry : ''
                      );
                      editorRef.current.undoManager.clear();
                    }}
                    initialValue={activeJournal ? activeJournal.entry : ''}
                    onEditorChange={(value, editor) => {
                      setEditorValue(value);
                    }}
                    init={{
                      height: '100%',
                      menubar: false,
                      branding: false,
                      plugins: [
                        'advlist',
                        'autolink',
                        'lists',
                        'link',
                        'image',
                        'charmap',
                        'preview',
                        'anchor',
                        'searchreplace',
                        'visualblocks',
                        'code',
                        'fullscreen',
                        'insertdatetime',
                        'media',
                        'table',
                        'help',
                        'wordcount',
                        'quickbars',
                      ],
                      toolbar:
                        'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | link image ',
                      image_title: true,
                      quickbars_image_toolbar:
                        'alignleft aligncenter alignright | rotateleft rotateright | imageoptions',
                      automatic_uploads: true,
                      images_upload_handler: imageUploadHandler,
                      content_style:
                        'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    }}
                  />
                </Box>
              </form>
            )}
          </Box>
        </VStack>
      </Box>
      <DeleteAlert
        isOpen={isOpenAlert}
        cancelRef={cancelRef}
        onClose={onCloseAlert}
        isDeleting={isDeleting}
        header={`Delete journal entry?`}
        body={
          "Are you sure? If you proceed with the deletion, it'll be gone forever."
        }
        onDeleteItem={onDeleteItem}
      />
    </React.Fragment>
  );
};

export default JournalItem;
